import { create } from 'zustand';
import { supabase } from '@/lib/supabase';
import { Bill } from '@/types';
import { trackChange } from '@/lib/utils/sync-tracker';

interface BillsState {
  bills: Bill[];
  isLoading: boolean;
  error: string | null;
  fetchBills: (patientId?: string) => Promise<void>;
  createBill: (billData: Omit<Bill, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Bill | null>;
  updateBill: (id: string, updates: Partial<Bill>) => Promise<void>;
  deleteBill: (id: string) => Promise<void>;
  getBillsByPatient: (patientId: string) => Bill[];
  clearError: () => void;
}

export const useBillsStore = create<BillsState>((set, get) => ({
  bills: [],
  isLoading: false,
  error: null,

  fetchBills: async (patientId?: string) => {
    set({ isLoading: true, error: null });
    try {
      let query = supabase
        .from('bills')
        .select('*')
        .order('created_at', { ascending: false });

      if (patientId) {
        query = query.eq('patient_id', patientId);
      }

      const { data, error } = await query;

      if (error) throw error;

      console.log('Bills fetch debug:', {
        patientId,
        rawData: data,
        dataLength: data?.length || 0
      });

      const bills: Bill[] = data.map(bill => ({
        id: bill.id,
        patientId: bill.patient_id,
        billNumber: bill.bill_number,
        billDate: bill.bill_date,
        services: bill.services || [],
        totalAmount: bill.total_amount,
        amountPaid: bill.amount_paid,
        paymentMode: bill.payment_mode,
        discountAmount: bill.discount_amount,
        discountPercentage: bill.discount_percentage,
        clinicDetails: bill.clinic_details || {},
        status: bill.status,
        createdAt: bill.created_at,
        updatedAt: bill.updated_at,
      }));

      console.log('Mapped bills:', bills);

      // Ensure bills are properly set even if empty
      set({ bills: bills || [], isLoading: false });
    } catch (error) {
      console.error('Error fetching bills:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to fetch bills', 
        isLoading: false 
      });
    }
  },

  createBill: async (billData) => {
    try {
      // Get current hospital_id for multi-tenant isolation
      let hospitalId: string | null = null;
      try {
        const { useHospital } = await import('../hooks/useHospital');
        const hospital = useHospital.getState().currentHospital;
        hospitalId = hospital?.id || null;
        console.log('🏥 Current hospital_id for bill:', hospitalId);
      } catch (e) {
        console.warn('⚠️ Could not get hospital_id for bill:', e);
      }

      if (!hospitalId) {
        throw new Error('Hospital ID is required to create a bill');
      }

      const billId = crypto.randomUUID();
      const { data, error } = await supabase
        .from('bills')
        .insert({
          id: billId,
          patient_id: billData.patientId,
          hospital_id: hospitalId, // Required for RLS policy
          bill_number: billData.billNumber,
          bill_date: billData.billDate,
          services: billData.services,
          total_amount: billData.totalAmount,
          amount_paid: billData.amountPaid,
          payment_mode: billData.paymentMode,
          discount_amount: billData.discountAmount,
          discount_percentage: billData.discountPercentage,
          clinic_details: billData.clinicDetails,
          status: billData.status,
        })
        .select()
        .single();

      if (error) throw error;

      const newBill: Bill = {
        id: data.id,
        patientId: data.patient_id,
        billNumber: data.bill_number,
        billDate: data.bill_date,
        services: data.services || [],
        totalAmount: data.total_amount,
        amountPaid: data.amount_paid,
        paymentMode: data.payment_mode,
        discountAmount: data.discount_amount,
        discountPercentage: data.discount_percentage,
        clinicDetails: data.clinic_details || {},
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };

      // Add to local state
      set(state => ({
        bills: [newBill, ...state.bills]
      }));

      // Track change for incremental sync
      trackChange({
        id: newBill.id,
        table: 'bills',
        operation: 'create',
        data: {
          patient_id: newBill.patientId,
          bill_number: newBill.billNumber,
          bill_date: newBill.billDate,
          services: newBill.services,
          total_amount: newBill.totalAmount,
          amount_paid: newBill.amountPaid,
          payment_mode: newBill.paymentMode,
          discount_amount: newBill.discountAmount,
          discount_percentage: newBill.discountPercentage,
          clinic_details: newBill.clinicDetails,
          status: newBill.status,
        }
      });

      return newBill;
    } catch (error) {
      console.error('Error creating bill:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create bill' 
      });
      return null;
    }
  },

  updateBill: async (id: string, updates: Partial<Bill>) => {
    try {
      const updateData: any = {};
      if (updates.billNumber) updateData.bill_number = updates.billNumber;
      if (updates.billDate) updateData.bill_date = updates.billDate;
      if (updates.services) updateData.services = updates.services;
      if (updates.totalAmount !== undefined) updateData.total_amount = updates.totalAmount;
      if (updates.amountPaid !== undefined) updateData.amount_paid = updates.amountPaid;
      if (updates.paymentMode) updateData.payment_mode = updates.paymentMode;
      if (updates.discountAmount !== undefined) updateData.discount_amount = updates.discountAmount;
      if (updates.discountPercentage !== undefined) updateData.discount_percentage = updates.discountPercentage;
      if (updates.clinicDetails) updateData.clinic_details = updates.clinicDetails;
      if (updates.status) updateData.status = updates.status;

      const { error } = await supabase
        .from('bills')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;

      // Update local state
      set(state => ({
        bills: state.bills.map(bill => 
          bill.id === id ? { ...bill, ...updates } : bill
        )
      }));

      // Track change for incremental sync
      trackChange({
        id,
        table: 'bills',
        operation: 'update',
        data: updateData
      });
    } catch (error) {
      console.error('Error updating bill:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update bill' 
      });
    }
  },

  deleteBill: async (id: string) => {
    try {
      const { error } = await supabase
        .from('bills')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Remove from local state
      set(state => ({
        bills: state.bills.filter(bill => bill.id !== id)
      }));

      // Track change for incremental sync
      trackChange({
        id,
        table: 'bills',
        operation: 'delete',
        data: {}
      });
    } catch (error) {
      console.error('Error deleting bill:', error);
      set({ 
        error: error instanceof Error ? error.message : 'Failed to delete bill' 
      });
    }
  },

  getBillsByPatient: (patientId: string) => {
    return get().bills.filter(bill => bill.patientId === patientId);
  },

  clearError: () => set({ error: null }),
})); 